import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, Variants } from "framer-motion";
import {
  FiBook,
  FiStar,
  FiEdit,
  FiLock,
  FiCompass,
  FiBarChart2,
} from "react-icons/fi";
import { IconType } from "react-icons";
import { Link } from "react-router-dom";

// Explicitly use IconType for react-icons compatibility
interface Tile {
  t: string;
  Icon: IconType;
  c: string;
  iconColor?: string;
  desc?: string;
}

export default function DeenLockLanding() {
  const [userName] = useState(() =>
    Math.random() < 0.5 ? "Mohamed" : "Fatma"
  );
  const [hoveredTile, setHoveredTile] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const appTiles: Tile[] = [
    { t: "Quran", Icon: FiBook, c: "bg-[#FFD369]/20", iconColor: "#FFD369" },
    { t: "Hadith", Icon: FiStar, c: "bg-[#FF7A59]/20", iconColor: "#FF7A59" },
    { t: "Journal", Icon: FiEdit, c: "bg-[#60F2FF]/20", iconColor: "#60F2FF" },
    {
      t: "Barakah Mode",
      Icon: FiLock,
      c: "bg-[#6BF59E]/20",
      iconColor: "#6BF59E",
    },
    { t: "Qibla", Icon: FiCompass, c: "bg-[#FF5FA2]/20", iconColor: "#FF5FA2" },
    {
      t: "Stats",
      Icon: FiBarChart2,
      c: "bg-[#8CFF60]/20",
      iconColor: "#8CFF60",
    },
  ];

  const mainTiles: Tile[] = [
    {
      t: "Read Quran",
      Icon: FiBook,
      c: "bg-[#FFD369]/20",
      desc: "Read your daily page or verse to unlock full access to your apps.",
    },
    {
      t: "Hadith of the Day",
      Icon: FiStar,
      c: "bg-[#FF7A59]/20",
      desc: "Explore authentic hadiths and receive a new one daily to learn.",
    },
    {
      t: "Journal",
      Icon: FiEdit,
      c: "bg-[#60F2FF]/20",
      desc: "Reflect on your day, goals, and struggles through journaling.",
    },
    {
      t: "Barakah Mode",
      Icon: FiLock,
      c: "bg-[#6BF59E]/20",
      desc: "Lock apps to limit screentime and focus on your reading goals.",
    },
    {
      t: "Qibla",
      Icon: FiCompass,
      c: "bg-[#FF5FA2]/20",
      desc: "Access prayer times and Qibla direction.",
    },
    {
      t: "Stats",
      Icon: FiBarChart2,
      c: "bg-[#8CFF60]/20",
      desc: "Track your overall progress and spiritual stats.",
    },
  ];

  const steps = [
    {
      title: "1. Set Your Goal",
      desc: "Choose daily pages or verses from the Quran you aim to complete.",
      icon: "🎯",
    },
    {
      title: "2. Select Distracting Apps",
      desc: "Pick the apps that distract you, and DeenLock will gently limit access.",
      icon: "📱",
    },
    {
      title: "3. Read & Unlock",
      desc: "As you progress towards your goal, blocked apps gradually become available.",
      icon: "🔓",
    },
    {
      title: "4. Build Your Streak",
      desc: "Maintain consistency to build daily streaks and unlock achievements.",
      icon: "🔥",
    },
  ];

  const features = [
    {
      t: "Page & Verse Modes",
      d: "Track progress by pages or verse-by-verse. Switch without losing progress.",
      icon: "📖",
    },
    {
      t: "Hadith of the Day",
      d: "Search authentic hadiths and receive a new hadith everyday to learn.",
      icon: "🕌",
    },
    {
      t: "Lock Selected Apps",
      d: "Temporarily restrict apps until the daily goal is met, fostering focus.",
      icon: "🔒",
    },
    {
      t: "Daily Streaks",
      d: "Encourages consistency with motivating streaks and spiritual achievements.",
      icon: "⚡",
    },
    {
      t: "Journaling",
      d: "Write down your daily goals, struggles, and reflections.",
      icon: "📝",
    },
    {
      t: "Privacy-first",
      d: "All your reading data stays securely on your device with stats synchronization.",
      icon: "🛡️",
    },
  ];

  const pricingPlans = [
    {
      name: "Free",
      price: "$0",
      desc: "Perfect for starting your spiritual journey.",
      features: [
        "Basic Quran Reading",
        "Hadith of the Day",
        "Qibla Finder",
        "Journaling",
      ],
      buttonText: "Get Started",
      buttonGradient: "from-[#FFD369] to-[#FF7A59]",
      textColor: "text-[#08101a]",
    },
    {
      name: "Premium",
      price: "$9.99 / Lifetime",
      desc: "Unlock Barakah Mode and all premium features.",
      features: [
        "Everything in Basic Mode",
        "Barakah Mode (App Locking)",
        "Advanced Quran Tracking",
        "Stats & Streaks",
      ],
      buttonText: "Go Premium",
      buttonGradient: "from-[#6BF59E] to-[#059669]",
      textColor: "text-white",
    },
  ];

  useEffect(() => {
    const interval = setInterval(
      () => setCurrentStep((prev) => (prev + 1) % steps.length),
      4000
    );
    return () => clearInterval(interval);
  }, [steps.length]);

  const featureCardVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
    hover: { scale: 1.05, boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.3)" },
  };

  // Handle smooth scrolling to a section
  const handleScroll = (sectionId: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      // Ensure the element is focused for accessibility
      element.focus({ preventScroll: true });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#4C5B7F] to-[#2C3B5F] text-white antialiased font-sans">
      <header className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Replace the old gradient SVG box with your logo image */}
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center shadow-glow overflow-hidden">
            <img
              src="/DeenLock.png"
              alt="DeenLock Logo"
              className="w-10 h-10 object-contain"
            />
          </div>

          <div>
            <div className="font-bold text-lg">DeenLock</div>
            <div className="text-xs text-white/60 -mt-0.5">
              Focus • Faith • Discipline
            </div>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-6 text-sm text-white/80">
          <Link
            to="#how-it-works"
            onClick={handleScroll("how-it-works")}
            className="hover:text-white transition"
          >
            How it works
          </Link>
          <Link
            to="#features"
            onClick={handleScroll("features")}
            className="hover:text-white transition"
          >
            Features
          </Link>
          <Link
            to="#pricing"
            onClick={handleScroll("pricing")}
            className="hover:text-white transition"
          >
            Pricing
          </Link>
          <button className="ml-4 px-4 py-2 rounded-lg bg-gradient-to-r from-[#6BF59E] to-[#059669] text-white font-semibold shadow-lg hover:shadow-xl transition-shadow duration-300">
            Download Coming Soon
          </button>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-6 pb-24 relative">
        <section className="grid lg:grid-cols-2 gap-12 items-center mt-10">
          <motion.div
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 20,
              duration: 0.7,
            }}
          >
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
              DeenLock — Lock distractions, unlock devotion
            </h1>
            <p className="mt-4 text-white/80 max-w-xl">
              An elegant focus app that gently limits access to selected apps
              until you complete your daily reading goal. Built for spiritual
              discipline, daily streaks, and mindful device use.
            </p>

            <div className="mt-8 flex gap-3">
<button
  type="button"
  className="inline-flex items-center gap-3 bg-gradient-to-r from-[#FFD369] to-[#FF7A59] text-[#08101a] font-semibold px-5 py-3 rounded-2xl shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
>
  Download Coming Soon
</button>
              <Link
                to="#how-it-works"
                onClick={handleScroll("how-it-works")}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#6BF59E] to-[#059669] text-white font-semibold px-5 py-3 rounded-2xl shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
              >
                See How it Works
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 20,
              duration: 0.9,
            }}
            className="flex justify-center"
          >
            <div className="w-[360px] h-[720px] rounded-3xl bg-gradient-to-b from-white/4 via-white/2 to-transparent border border-white/6 shadow-2xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#08101a]/60 to-transparent" />
              <div className="absolute -left-20 -top-20 w-64 h-64 blur-3xl bg-gradient-to-r from-[#FFB86B] to-[#FF7A59] opacity-20 rounded-full"></div>
              <div className="absolute right-6 bottom-8 w-48 h-48 blur-2xl bg-gradient-to-r from-[#60F2FF] to-[#6BF59E] opacity-10 rounded-full"></div>

              <div className="bg-white/6 p-4 rounded-2xl border border-white/8 flex flex-col h-full">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 rounded-md bg-white/8" />
                  <div className="text-xs text-white/80">Today</div>
                  <div className="w-8 h-8 rounded-md bg-white/8" />
                </div>

                <div className="mt-4 text-center">
                  <p className="text-lg font-semibold">Welcome back!</p>
                  <p className="text-2xl font-bold -mt-1">{userName}</p>
                </div>

                <div className="mt-4 p-3 rounded-xl bg-gradient-to-r from-[#0f172a]/60 to-[#071033]/40 border border-white/4 text-center">
                  <motion.div
                    className="text-4xl"
                    animate={{
                      scale: [1, 1.2],
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{
                      scale: {
                        type: "spring",
                        stiffness: 200,
                        damping: 20,
                        duration: 1.5,
                        repeat: Infinity,
                      },
                      rotate: {
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      },
                    }}
                  >
                    🔥
                  </motion.div>
                  <div className="text-2xl font-bold mt-1">3-Day Streak!</div>
                  <div className="text-xs text-white/60 mt-1">
                    Keep up the momentum!
                  </div>
                </div>

                <div className="mt-4 flex-grow grid grid-cols-2 gap-3 content-start">
                  {appTiles.map((tile, i) => {
                    const Icon = tile.Icon as React.ComponentType<
                      React.SVGProps<SVGSVGElement>
                    >;
                    if (!Icon) return null;
                    return (
                      <motion.div
                        key={i}
                        className={`${tile.c} rounded-xl p-3 text-center text-sm font-semibold backdrop-blur-sm border border-white/6 flex flex-col items-center justify-center shadow-md`}
                        whileHover={{ scale: 1.05 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: i * 0.08,
                          duration: 0.5,
                          type: "spring",
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <div
                          className="text-3xl mb-1"
                          style={{ color: tile.iconColor || "#FFFFFF" }}
                        >
                          <Icon />
                        </div>
                        {tile.t}
                      </motion.div>
                    );
                  })}
                </div>

                <div className="mt-4 flex gap-3">
                  <button className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#FFD369] to-[#FF7A59] text-[#08101a] font-semibold shadow-lg">
                    Start Reading
                  </button>
                </div>
              </div>

              <div className="absolute -bottom-8 left-6 text-xs text-white/30">
                DeenLock preview
              </div>
            </div>
          </motion.div>
        </section>

        <section className="mt-20">
          <h2 className="text-3xl font-bold text-center mb-10">
            Discover DeenLock
          </h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {mainTiles.map((tile, i) => {
              const Icon = tile.Icon as React.ComponentType<
                React.SVGProps<SVGSVGElement>
              >;
              if (!Icon) return null;
              return (
                <motion.div
                  key={i}
                  className={`${tile.c} rounded-2xl p-6 text-center text-lg font-semibold backdrop-blur-sm border border-white/10 flex flex-col items-center justify-center shadow-xl cursor-pointer group`}
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: i * 0.08,
                    duration: 0.6,
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                  }}
                  onMouseEnter={() => setHoveredTile(tile.t)}
                  onMouseLeave={() => setHoveredTile(null)}
                >
                  <div className="text-5xl mb-3 text-white/90 group-hover:text-[#FFD369] transition-colors duration-300">
                    <Icon />
                  </div>
                  <div>{tile.t}</div>
                  {hoveredTile === tile.t && tile.desc && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs text-white/70 mt-2 px-2 py-1 bg-black/20 rounded"
                      transition={{
                        duration: 0.3,
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                      }}
                    >
                      {tile.desc}
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>

        <section id="how-it-works" className="mt-24">
          <motion.h2
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.6,
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            className="text-3xl font-bold text-center mb-12"
          >
            How It Works
          </motion.h2>
          <div className="relative max-w-4xl mx-auto overflow-hidden rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                  duration: 0.5,
                }}
                className="p-8 text-center flex flex-col items-center justify-center"
              >
                <div className="text-6xl mb-4">{steps[currentStep].icon}</div>
                <div className="font-bold text-xl mb-2 font-serif">
                  {steps[currentStep].title}
                </div>
                <div className="text-sm text-white/70 max-w-md">
                  {steps[currentStep].desc}
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="flex justify-center gap-3 mt-8 pb-4">
              {steps.map((_, i) => (
                <motion.div
                  key={i}
                  className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-300 ${
                    i === currentStep ? "bg-white/90 scale-125" : "bg-white/30"
                  }`}
                  whileHover={{ scale: 1.3 }}
                  onClick={() => setCurrentStep(i)}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                />
              ))}
            </div>
          </div>
        </section>

        <section id="features" className="mt-24">
          <motion.h2
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.6,
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            className="text-3xl font-bold text-center mb-12"
          >
            Powerful Features
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <motion.div
                key={i}
                variants={featureCardVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                className="p-6 rounded-2xl bg-white/5 border border-white/8 backdrop-blur-sm text-center flex flex-col items-center shadow-lg cursor-pointer hover:bg-white/10"
              >
                <div className="text-5xl mb-4">{f.icon}</div>
                <div className="text-xl font-semibold mb-2">{f.t}</div>
                <div className="text-sm text-white/70">{f.d}</div>
              </motion.div>
            ))}
          </div>
        </section>

        <section id="pricing" className="mt-24">
          <motion.h2
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.6,
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            className="text-3xl font-bold text-center mb-12"
          >
            Pricing Plans
          </motion.h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {pricingPlans.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: i * 0.2,
                  duration: 0.6,
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                }}
                className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm flex flex-col items-center shadow-lg"
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.3)",
                }}
              >
                <div className="text-2xl font-bold mb-2">{plan.name}</div>
                <div className="text-4xl font-extrabold mb-2">{plan.price}</div>
                <div className="text-sm text-white/70 mb-4">{plan.desc}</div>
                <ul className="text-sm text-white/80 mb-6 space-y-2">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2">
                      <span className="text-[#FFD369]">✓</span> {feature}
                    </li>
                  ))}
                </ul>
                <button
                  className={`px-6 py-3 rounded-xl bg-gradient-to-r ${plan.buttonGradient} ${plan.textColor} font-semibold shadow-lg hover:shadow-xl transition-all duration-300`}
                >
                  {plan.buttonText}
                </button>
              </motion.div>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 py-8 mt-20">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-white/60">
          <div className="text-sm">
            © {new Date().getFullYear()} DeenLock — All rights reserved.
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/terms-of-use"
              className="text-sm hover:text-white transition"
            >
              Terms of Use
            </Link>
            <Link
              to="/privacy-policy"
              className="text-sm hover:text-white transition"
            >
              Privacy Policy
            </Link>
            <a
              href="mailto:deenlockapp@gmail.com"
              className="text-sm hover:text-white transition"
            >
              deenlockapp@gmail.com
            </a>
          </div>
        </div>
      </footer>

      <style>{`
        .shadow-glow { box-shadow: 0 8px 20px rgba(255, 138, 70, 0.12); }
        html { scroll-behavior: smooth; }
      `}</style>
    </div>
  );
}
